import { useCallback, useEffect, useState } from "react";
import { ApiError } from "@forcebase-ai/api-client";

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
}

/** Loads async data on mount / when `deps` change, with a manual reload. Error messages are the
 * API's PHI-safe message only. */
export function useAsync<T>(fn: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);

  const run = useCallback(() => {
    let active = true;
    setLoading(true);
    setError(null);
    fn()
      .then((d) => active && setData(d))
      .catch((e: unknown) => {
        if (!active) return;
        setError(e instanceof ApiError ? e.message : "Something went wrong");
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
    // Keyed off caller-provided deps + reload nonce (fn identity is unstable by design).
  }, [...deps, nonce]);

  useEffect(run, [run]);

  return { data, error, loading, reload: () => setNonce((n) => n + 1) };
}
